import { newElementWith } from "../../excalidraw/element/mutateElement";
import type {
  ExcalidrawElement,
  ExcalidrawLinearElement,
  PointBinding,
} from "../../excalidraw/element/types";

import type { MindmapCustomData } from "./mindmapElements";

/**
 * Native arrow binding for mindmap connectors (V3).
 *
 * Mindmap connectors used to be deliberately UNBOUND so that
 * `mindmapConnectorGeometry` stayed the sole authority on their route. The cost
 * was that dragging a pill left its arrows behind until a post-gesture reroute
 * caught up — which reads as broken, because the arrows only snap back once the
 * pointer is released. Excalidraw's own binding is what makes an arrow track a
 * shape LIVE during the drag, and `mindmapNode` is already a bindable element
 * type, so the connectors take the binding and the reroute pass demotes to a
 * cleanup that restores the canonical elbow once the gesture ends.
 *
 * The two layers do not fight: binding only ever moves the arrow's END POINTS
 * (`updateBoundElements` → `updateBoundPoint`), so mid-drag the elbow bends;
 * the pointerup pass then rewrites the whole point list, and the bindings are
 * recomputed from those fresh points on the next drag.
 *
 * Everything here is pure — it returns the next element array (or null when
 * nothing changed), so a caller can commit it in one `updateScene`.
 */

/**
 * Focus 0 aims the binding at the bound pill's CENTER, and the elbow route
 * departs/enters at exactly the vertical center of each pill, so the bound
 * endpoint Excalidraw computes lands on the same point the route would have
 * chosen. Any other focus would drag the endpoint off the route the moment the
 * user touches a pill.
 */
const MINDMAP_BINDING_FOCUS = 0;

/**
 * Distance the bound endpoint is held off the pill's border. Excalidraw treats
 * gap 0 as "point at the focus point itself" (i.e. inside the shape); 1 is its
 * own minimum and keeps the arrow tip just clear of the pill outline, matching
 * the route's edge anchor to within a pixel.
 */
const MINDMAP_BINDING_GAP = 1;

const mindmapOf = (el: ExcalidrawElement): MindmapCustomData | undefined =>
  (el.customData?.mindmap as MindmapCustomData | undefined) ?? undefined;

/** Key a node by map AND block: two maps of the same page share blockIds. */
export const nodeKey = (mapId: string, blockId: string): string =>
  `${mapId}::${blockId}`;

const bindingTo = (elementId: string): PointBinding => ({
  elementId,
  focus: MINDMAP_BINDING_FOCUS,
  gap: MINDMAP_BINDING_GAP,
});

const sameBinding = (
  current: PointBinding | null | undefined,
  next: PointBinding,
): boolean =>
  !!current &&
  current.elementId === next.elementId &&
  current.focus === next.focus &&
  current.gap === next.gap;

/**
 * The parent and child pill a connector runs between, or null when either end
 * is missing from the scene (a half-restored subtree, a legacy map with no
 * `parentBlockId` links). A connector is keyed by the CHILD block it points at,
 * and the child element carries the parent link, so a reparent is followed for
 * free.
 */
const endpointsOf = (
  connector: ExcalidrawElement,
  nodes: ReadonlyMap<string, ExcalidrawElement>,
): { parent: ExcalidrawElement; child: ExcalidrawElement } | null => {
  const mm = mindmapOf(connector);
  if (!mm) {
    return null;
  }
  const child = nodes.get(nodeKey(mm.mapId, mm.blockId));
  const parentBlockId = child && mindmapOf(child)?.parentBlockId;
  const parent = parentBlockId
    ? nodes.get(nodeKey(mm.mapId, parentBlockId))
    : undefined;
  return child && parent ? { parent, child } : null;
};

/**
 * Live mindmap pills of every map on the scene, indexed by map + block.
 *
 * A key is supposed to be unique, and two things break it: duplicating a PARTIAL
 * selection keeps the original `mapId` on purpose (see `planMindmapRemint`), so
 * two pills claim one key inside ONE live tree; a scene saved before duplication
 * re-minted `mapId` holds two WHOLE maps sharing one, where every key is
 * contested.
 *
 * Dropping a contested key is only right for the second. `endpointsOf` resolves
 * both the child and the parent lookup here, so a dropped key unbinds the copied
 * pill's connector AND the whole fan below it — permanently, since the reroute
 * pass reads the same index. The claimants' PARENT key tells the cases apart: a
 * stray copy hangs off a parent only one pill claims, so the anchored claimant
 * wins. When every claimant's parent is contested too, no local choice is right
 * and the key is dropped, leaving both maps' arrows where they are.
 */
export const indexMindmapNodes = (
  scene: readonly ExcalidrawElement[],
): Map<string, ExcalidrawElement> => {
  const claims = new Map<string, ExcalidrawElement[]>();
  for (const el of scene) {
    const mm = mindmapOf(el);
    if (mm?.role === "node" && !el.isDeleted) {
      const key = nodeKey(mm.mapId, mm.blockId);
      const claimants = claims.get(key);
      if (claimants) {
        claimants.push(el);
      } else {
        claims.set(key, [el]);
      }
    }
  }

  const uncontestedParent = (el: ExcalidrawElement): boolean => {
    const mm = mindmapOf(el)!;
    return (
      !!mm.parentBlockId &&
      claims.get(nodeKey(mm.mapId, mm.parentBlockId))?.length === 1
    );
  };

  const nodes = new Map<string, ExcalidrawElement>();
  for (const [key, claimants] of claims) {
    if (claimants.length === 1) {
      nodes.set(key, claimants[0]);
      continue;
    }
    const anchored = claimants.find(uncontestedParent);
    if (anchored) {
      nodes.set(key, anchored);
    }
  }
  return nodes;
};

/**
 * Bind every live mindmap connector to its parent and child pills, and register
 * the arrow in both pills' `boundElements`.
 *
 * This is BOTH the emit-time binding step (every creation path routes through
 * it, so generation, expand, restore and refresh can never disagree) and the
 * migration for scenes generated before connectors were bound: a map loaded
 * from localStorage / the cloud arrives with null bindings and null
 * `boundElements`, and one pass over it is all the repair needs. Idempotent —
 * an already-bound scene returns null, which is the caller's loop guard.
 */
export const bindMindmapConnectors = (
  scene: readonly ExcalidrawElement[],
): ExcalidrawElement[] | null => {
  const nodes = indexMindmapNodes(scene);
  if (nodes.size === 0) {
    return null;
  }

  // Arrow ids each pill must list in `boundElements`, collected before the
  // rewrite so a pill gains all of its connectors in a single update.
  const arrowsByNodeId = new Map<string, Set<string>>();
  const bindingsByConnectorId = new Map<
    string,
    { start: PointBinding; end: PointBinding }
  >();

  for (const el of scene) {
    const mm = mindmapOf(el);
    if (mm?.role !== "connector" || el.isDeleted) {
      continue;
    }
    const ends = endpointsOf(el, nodes);
    if (!ends) {
      continue;
    }
    bindingsByConnectorId.set(el.id, {
      start: bindingTo(ends.parent.id),
      end: bindingTo(ends.child.id),
    });
    for (const node of [ends.parent, ends.child]) {
      const set = arrowsByNodeId.get(node.id) ?? new Set<string>();
      set.add(el.id);
      arrowsByNodeId.set(node.id, set);
    }
  }

  let changed = false;
  const next = scene.map((el) => {
    const bindings = bindingsByConnectorId.get(el.id);
    if (bindings) {
      const linear = el as ExcalidrawLinearElement;
      if (
        sameBinding(linear.startBinding, bindings.start) &&
        sameBinding(linear.endBinding, bindings.end)
      ) {
        return el;
      }
      changed = true;
      return newElementWith(linear, {
        startBinding: bindings.start,
        endBinding: bindings.end,
      });
    }

    const arrows = arrowsByNodeId.get(el.id);
    if (!arrows) {
      return el;
    }
    const existing = el.boundElements ?? [];
    const missing = [...arrows].filter(
      (id) => !existing.some((bound) => bound.id === id),
    );
    if (missing.length === 0) {
      return el;
    }
    changed = true;
    return newElementWith(el, {
      boundElements: [
        ...existing,
        ...missing.map((id) => ({ id, type: "arrow" as const })),
      ],
    });
  });

  return changed ? next : null;
};

/** `bindMindmapConnectors`, flattened for the pure emit paths that always want
 * an array back rather than a "nothing changed" signal. */
export const withMindmapBindings = (
  scene: readonly ExcalidrawElement[],
): ExcalidrawElement[] => bindMindmapConnectors(scene) ?? [...scene];
